import { useState, useEffect, useRef } from 'react';
import { WifiOff, X, RefreshCw, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useConnectivity } from '@/hooks/useConnectivity';

const STORAGE_KEY = 'connectivity-banner-disabled';

export const OfflineBanner = () => {
  const { isOffline, isDegraded, isInitializing, recheckNow, lastCheckedAt } = useConnectivity();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isPermanentlyDismissed, setIsPermanentlyDismissed] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [isRetrying, setIsRetrying] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  
  // Track when status last changed for stability check
  const lastStatusRef = useRef<{ isOffline: boolean; isDegraded: boolean }>({ isOffline: false, isDegraded: false });
  const statusStableSinceRef = useRef<number>(Date.now());
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountTimeRef = useRef<number>(Date.now());

  const MOUNT_GRACE_PERIOD_MS = 10000; // Don't show banner within 10s of mount (increased from 5s)
  const STATUS_STABILITY_MS = 5000; // Status must be stable for 5s before showing (increased from 3s)

  const handleRetry = async () => {
    setIsRetrying(true);
    await recheckNow();
    setIsRetrying(false);
  };

  // Track status changes and stability
  useEffect(() => {
    const statusChanged = 
      lastStatusRef.current.isOffline !== isOffline || 
      lastStatusRef.current.isDegraded !== isDegraded;
    
    if (statusChanged) {
      lastStatusRef.current = { isOffline, isDegraded };
      statusStableSinceRef.current = Date.now();
    }
  }, [isOffline, isDegraded]);

  // Control banner visibility with stability checks
  useEffect(() => {
    // Clear any pending timer
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }

    // Don't show banner during initialization
    if (isInitializing) {
      setShowBanner(false);
      return;
    }

    // Hide banner immediately when online
    if (!isOffline && !isDegraded) {
      setShowBanner(false);
      return;
    }

    // Don't show banner if we haven't done initial check yet
    if (!lastCheckedAt) {
      setShowBanner(false);
      return;
    }

    // Calculate delays
    const msSinceMount = Date.now() - mountTimeRef.current;
    const msSinceStatusChange = Date.now() - statusStableSinceRef.current;
    
    // Don't show during mount grace period
    if (msSinceMount < MOUNT_GRACE_PERIOD_MS) {
      const remainingGrace = MOUNT_GRACE_PERIOD_MS - msSinceMount;
      showTimerRef.current = setTimeout(() => {
        // Re-check conditions after grace period
        if ((isOffline || isDegraded) && !isInitializing) {
          setShowBanner(true);
        }
      }, remainingGrace + STATUS_STABILITY_MS);
      return;
    }

    // Wait for status to be stable before showing
    if (msSinceStatusChange < STATUS_STABILITY_MS) {
      const remainingStability = STATUS_STABILITY_MS - msSinceStatusChange;
      showTimerRef.current = setTimeout(() => {
        // Re-check conditions after stability period
        if ((isOffline || isDegraded) && !isInitializing) {
          setShowBanner(true);
        }
      }, remainingStability);
      return;
    }

    // Status is stable and past grace period - show banner
    setShowBanner(true);
    
    return () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
  }, [isOffline, isDegraded, isInitializing, lastCheckedAt]);

  // Reset dismissed state when transitioning from online to offline (but not permanent)
  useEffect(() => {
    if ((isOffline || isDegraded) && !isPermanentlyDismissed) {
      setIsDismissed(false);
    }
  }, [isOffline, isDegraded, isPermanentlyDismissed]);

  const handlePermanentDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsPermanentlyDismissed(true);
    setIsDismissed(true);
  };
  
  if (isPermanentlyDismissed) return null;
  if (isDismissed) return null;
  if (!showBanner) return null;

  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-50',
        'py-2 px-4',
        'flex items-center justify-center gap-2',
        isOffline 
          ? 'bg-destructive text-destructive-foreground' 
          : 'bg-yellow-500 text-yellow-950'
      )}
      role="alert"
    >
      <WifiOff className="h-4 w-4" />
      <span className="text-sm font-medium">
        {isOffline 
          ? "You're offline. Some features may be unavailable."
          : "Your connection is unstable. Some features may be slow."}
      </span>
      <button
        onClick={handleRetry}
        disabled={isRetrying}
        className={cn(
          'ml-2 p-1 rounded flex items-center gap-1 text-xs',
          isOffline 
            ? 'hover:bg-destructive-foreground/10' 
            : 'hover:bg-yellow-600/20'
        )}
        aria-label="Retry connection"
      >
        <RefreshCw className={cn('h-3 w-3', isRetrying && 'animate-spin')} />
        Retry
      </button>
      <button
        onClick={handlePermanentDismiss}
        className={cn(
          'ml-2 p-1 rounded flex items-center gap-1 text-xs',
          isOffline 
            ? 'hover:bg-destructive-foreground/10' 
            : 'hover:bg-yellow-600/20'
        )}
        aria-label="Don't show again"
        title="Don't show connectivity warnings"
      >
        <EyeOff className="h-3 w-3" />
        Hide
      </button>
      <button
        onClick={() => setIsDismissed(true)}
        className={cn(
          'absolute right-4 p-1 rounded',
          isOffline 
            ? 'hover:bg-destructive-foreground/10' 
            : 'hover:bg-yellow-600/20'
        )}
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
